// src/components/auth/ResetPassword.js

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../../firebase/firebase'; // Make sure this path is correct

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode');

  const [newPassword, setNewPassword] = useState('');
  const [promptMessage, setPromptMessage] = useState(''); // For the "Enter password" text
  const [successMessage, setSuccessMessage] = useState(''); // For the "Success!" text
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // 1. On page load, verify the code is valid
  useEffect(() => {
    if (!oobCode) {
      setError('No reset code provided. Please check your link.');
      setIsLoading(false);
      return;
    }

    // Check if the code is valid before showing the form
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        setPromptMessage(`Enter a new password for ${email}`);
        setIsLoading(false);
      })
      .catch((err) => {
        setError('The link is invalid or has expired. Please try resetting your password again.');
        setIsLoading(false);
      });
  }, [oobCode]);

  // 2. Handle the form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!oobCode) {
      setError('Invalid link. Please request a new one.');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      // This function is what "uses" the code
      await confirmPasswordReset(auth, oobCode, newPassword);

      setSuccessMessage('Your password has been reset successfully! Redirecting to log in...');
      setPromptMessage(''); // Clear prompt

      // Redirect to login after a few seconds
      setTimeout(() => navigate('/login'), 3000); // Adjust '/login' if needed
    } catch (err) {
      setError('Failed to reset password. The link may have expired or was already used.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !promptMessage && !error) {
    return (
      <div className="text-center p-10">
        <p>Verifying your link...</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-xl">
      <h2 className="text-2xl font-semibold mb-4">Reset Your Password</h2>

      {error && ( // Show error if it exists
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {successMessage && ( // Show success message if it exists
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {/* Show prompt message ONLY if there is no success or error */}
      {promptMessage && !successMessage && !error && (
        <p className="text-slate-700 mb-4">{promptMessage}</p>
      )}

      {/* Show form ONLY if there is no success message */}
      {!successMessage && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-700"
            >
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-lg shadow-md disabled:bg-gray-400"
          >
            {isLoading ? 'Saving...' : 'Save New Password'}
          </button>
        </form>
      )}
    </div>
  );
};

export default ResetPassword;